import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { QuillModule } from 'ngx-quill';
import { FormService } from '../service/form.service';
import { FormTemplate, FormTab, FormField, FormFieldType, FormStatus, CreateFormRequest, UpdateFormRequest, TextInputType } from '../models/form.model';
import { FormRendererComponent } from '../renderer/form-renderer.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TransPipe } from '../../../helpers/trans/trans.pipe';
import { AlertboxComponent } from '../../../helpers/alertbox/alertbox.component';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-form-builder',
  standalone: true,
  imports: [CommonModule, FormsModule, DragDropModule, MatTooltipModule, QuillModule, TransPipe, AlertboxComponent],
  templateUrl: './form-builder.component.html',
  styleUrls: ['./form-builder.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class FormBuilderComponent implements OnInit, OnChanges {
  @Input() organizationId!: string;
  @Input() templateId?: string; // For edit mode
  @Output() formSaved = new EventEmitter<void>();
  @Output() formPublished = new EventEmitter<void>();

  templateName = '';
  templateDescription = '';
  tabs: FormTab[] = [];
  currentTabIndex = 0;
  fieldTypes: FormFieldType[] = ['text', 'select-one', 'select-multiple', 'file', 'image', 'description', 'image-display'];
  editingTabId: string | null = null;
  editingTabTitle = '';

  // Publish workflow state
  isPublishing = false;
  isReadOnly = false;
  isCreatingDraft = false;
  isArchiving = false;
  templateVersion = 1;
  templateStatus: FormStatus = 'draft';
  textInputTypes: TextInputType[] = ['richtext', 'plaintext', 'singleline'];

  // Success/Error feedback for app-alertbox
  showsuccessarchive = false;
  showerrorarchive = false;
  showconfirmarchive = false;
  showconfirmdraft = false;
  showsuccessdraft = false;
  showerrordraft = false;
  showconfirmpublish = false;
  showsuccesssave = false;
  showwarningname = false;
  errorMessage = '';

  quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link']
    ]
  };

  get currentTab(): FormTab | undefined {
    return this.tabs[this.currentTabIndex];
  }

  constructor(private formService: FormService, private dialog: MatDialog) {
    this.initializeForm();
  }

  ngOnInit(): void {
    if (this.templateId) {
      this.loadTemplate();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['templateId'] && !changes['templateId'].firstChange) {
      if (this.templateId) {
        this.loadTemplate();
      } else {
        this.initializeForm();
      }
    }
  }

  openPreviewModal(): void {
    // Create a preview template from current builder state
    const previewTemplate: FormTemplate = {
      id: this.templateId || 'preview',
      organizationId: this.organizationId,
      name: this.templateName || 'Untitled Form',
      description: this.templateDescription,
      status: this.templateStatus,
      version: this.templateVersion,
      tabs: this.tabs,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '95vw';
    dialogConfig.height = '95vh';
    dialogConfig.maxWidth = '1400px';
    dialogConfig.panelClass = ['form-modal-xl'];
    dialogConfig.disableClose = false;
    dialogConfig.data = {
      template: previewTemplate,
      organizationId: this.organizationId,
      context: 'form-builder'
    };

    this.dialog.open(FormRendererComponent, dialogConfig);
  }

  private initializeForm(): void {
    // Clear overall template state for a fresh builder
    this.templateId = undefined;
    this.templateName = '';
    this.templateDescription = '';
    this.templateVersion = 1;
    this.templateStatus = 'draft';

    // Reset UI/editor state
    this.cancelTabEditing();
    this.tabs = [{ id: this.generateId(), title: 'Tab 1', order: 0, fields: [] }];
    this.currentTabIndex = 0;
    this.isReadOnly = false;

    // Reset workflow flags and feedback states
    this.isPublishing = false;
    this.isCreatingDraft = false;
    this.isArchiving = false;
    this.showsuccessarchive = false;
    this.showerrorarchive = false;
    this.showconfirmarchive = false;
    this.showconfirmdraft = false;
    this.showsuccessdraft = false;
    this.showerrordraft = false;
    this.showconfirmpublish = false;
    this.showsuccesssave = false;
    this.showwarningname = false;
    this.errorMessage = '';
  }

  loadTemplate(): void {
    if (!this.templateId) return;
    console.log('Loading template:', this.templateId);

    // Check if templateId contains version info (format: template_<id>_v<version>)
    const isFullSk1 = this.templateId.startsWith('template_');
    const actualTemplateId = isFullSk1 ? this.templateId.split('_')[1] : this.templateId;
    const requestedSk1 = isFullSk1 ? this.templateId : null;

    this.formService
      .getTemplate(this.organizationId, actualTemplateId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (templates) => {
          console.log('Raw template from API:', templates);
          if (templates && templates.length > 0) {
            let template: any;

            if (requestedSk1) {
              // If we have full sk1, match exactly by sk1
              template = templates.find(t => (t as any).sk1 === requestedSk1);
              console.log('Matching by sk1:', requestedSk1, 'found:', !!template);
            }

            // Fallback: find draft version, or published, or first available
            if (!template) {
              template = templates.find(t => t.status === 'draft') ||
                templates.find(t => t.status === 'published') ||
                templates[0];
              console.log('Using fallback template selection, status:', template?.status, 'version:', template?.version);
            }

            // Update templateId to the actual ID (without prefix/version) for API calls
            this.templateId = actualTemplateId;

            this.templateName = template.name || '';
            this.templateDescription = template.description || '';
            this.templateVersion = template.version || 1;
            this.templateStatus = template.status || 'draft';
            // Parse tabs if it's a JSON string
            this.tabs = typeof template.tabs === 'string'
              ? JSON.parse(template.tabs)
              : (template.tabs || []);

            this.cancelTabEditing();

            // Ensure image fields have imageConfig initialized
            this.ensureImageConfigInitialized();

            //console.log('Loaded template data:', {
            //   name: this.templateName,
            //   description: this.templateDescription,
            //   version: this.templateVersion,
            //   status: this.templateStatus,
            //   tabs: this.tabs
            // });
            this.currentTabIndex = 0;
            // Set read-only mode if template is published or archived
            this.isReadOnly = this.templateStatus === 'published' || this.templateStatus === 'archived';
          }
        },
        error: (err) => {
          console.error('Error loading template:', err);
          alert('Failed to load form template');
        }
      });
  }

  addTab(): void {
    const newTab: FormTab = {
      id: this.generateId(),
      title: `Tab ${this.tabs.length + 1}`,
      order: this.tabs.length,
      fields: []
    };
    this.tabs.push(newTab);
    this.selectTab(this.tabs.length - 1);
  }

  removeTab(index: number, event: Event): void {
    event.stopPropagation();
    if (this.tabs.length > 1) {
      const removedTabId = this.tabs[index].id;
      this.tabs.splice(index, 1);
      if (this.editingTabId === removedTabId) {
        this.cancelTabEditing();
      }
      if (this.currentTabIndex >= this.tabs.length) {
        this.currentTabIndex = this.tabs.length - 1;
      }
    }
  }

  selectTab(index: number): void {
    this.currentTabIndex = index;
    if (this.editingTabId && this.tabs[index]?.id !== this.editingTabId) {
      this.cancelTabEditing();
    }
  }

  startEditingTab(tab: FormTab, event: Event): void {
    event.stopPropagation();
    if (this.isReadOnly) return;
    this.editingTabId = tab.id;
    this.editingTabTitle = tab.title;
  }

  saveTabTitle(tab: FormTab, event?: Event): void {
    if (event) event.stopPropagation();
    if (this.isReadOnly || this.editingTabId !== tab.id) return;
    const trimmedTitle = this.editingTabTitle.trim();
    if (trimmedTitle) {
      tab.title = trimmedTitle;
    }
    this.cancelTabEditing();
  }

  cancelTabEditing(): void {
    this.editingTabId = null;
    this.editingTabTitle = '';
  }

  isTabEditing(tab: FormTab): boolean {
    return this.editingTabId === tab.id;
  }

  addFieldToCurrentTab(fieldType: FormFieldType): void {
    const tab = this.currentTab;
    if (!tab) return;
    const newField: FormField = {
      id: this.generateId(),
      type: fieldType,
      label: `${this.getFieldTypeLabel(fieldType)} #${tab.fields.length + 1}`,
      required: false,
      helpText: '',
      pattern: '',
      options: fieldType === 'select-one' || fieldType === 'select-multiple' ? [{ value: '', label: '' }] : undefined,
      inputType: fieldType === 'text' ? 'richtext' : undefined,
      imageConfig: fieldType === 'image' ? {
        imageWidth: 400,
        aspectRatio: 'free',
        outputFormat: 'png',
        maxFileSize: 5242880, // 5MB in bytes
        acceptedFileTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
      } : undefined
    };
    tab.fields.push(newField);
  }

  removeField(index: number): void {
    if (this.currentTab) {
      this.currentTab.fields.splice(index, 1);
    }
  }

  isSelectField(fieldType: FormFieldType): boolean {
    return fieldType === 'select-one' || fieldType === 'select-multiple';
  }

  addOption(fieldIndex: number): void {
    if (this.currentTab && this.currentTab.fields[fieldIndex]) {
      const field = this.currentTab.fields[fieldIndex];
      if (!field.options) field.options = [];
      field.options.push({ value: '', label: '' });
    }
  }

  removeOption(fieldIndex: number, optionIndex: number): void {
    if (this.currentTab && this.currentTab.fields[fieldIndex]) {
      const field = this.currentTab.fields[fieldIndex];
      if (field.options) {
        field.options.splice(optionIndex, 1);
      }
    }
  }

  onFieldReordered(event: any): void {
    // event has: previousIndex, currentIndex, item, container, previousContainer, isPointerOverContainer, distance, dropPoint
    if (!this.currentTab || !this.currentTab.fields) return;

    const { previousIndex, currentIndex } = event;

    // No-op if dropped in the same position
    if (previousIndex === currentIndex) return;

    // Remove from previous position and insert at new position
    const [movedField] = this.currentTab.fields.splice(previousIndex, 1);
    this.currentTab.fields.splice(currentIndex, 0, movedField);
  }

  isFileTypeAccepted(field: FormField, fileType: string): boolean {
    return field.imageConfig?.acceptedFileTypes?.includes(fileType) || false;
  }

  toggleFileType(field: FormField, fileType: string, event: any): void {
    if (!field.imageConfig) {
      field.imageConfig = {
        imageWidth: 400,
        aspectRatio: 'free',
        outputFormat: 'png',
        maxFileSize: 5242880,
        acceptedFileTypes: []
      };
    }
    if (!field.imageConfig.acceptedFileTypes) {
      field.imageConfig.acceptedFileTypes = [];
    }

    if (event.target.checked) {
      if (!field.imageConfig.acceptedFileTypes.includes(fileType)) {
        field.imageConfig.acceptedFileTypes.push(fileType);
      }
    } else {
      const index = field.imageConfig.acceptedFileTypes.indexOf(fileType);
      if (index > -1) {
        field.imageConfig.acceptedFileTypes.splice(index, 1);
      }
    }
  }

  getImageMaxFileSizeMB(field: FormField): number {
    const bytes = field.imageConfig?.maxFileSize || 5242880;
    return parseFloat((bytes / 1048576).toFixed(2));
  }

  setImageMaxFileSizeMB(field: FormField, mb: number): void {
    if (!field.imageConfig) {
      field.imageConfig = {
        imageWidth: 400,
        aspectRatio: 'free',
        outputFormat: 'png',
        maxFileSize: 5242880,
        acceptedFileTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
      };
    }
    field.imageConfig.maxFileSize = Math.round(mb * 1048576);
  }

  private ensureImageConfigInitialized(): void {
    this.tabs.forEach(tab => {
      tab.fields?.forEach(field => {
        if (field.type === 'image' && !field.imageConfig) {
          field.imageConfig = {
            imageWidth: 400,
            aspectRatio: 'free',
            outputFormat: 'png',
            maxFileSize: 5242880,
            acceptedFileTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
          };
        }
      });
    });
  }

  saveDraft(): void {
    if (!this.templateName) {
      this.errorMessage = 'Please enter a form name';
      this.showwarningname = true;
      setTimeout(() => this.showwarningname = false, 2000);
      return;
    }
    if (this.templateId) {
      this.updateTemplate();
    } else {
      this.createTemplate();
    }
  }

  publishForm(): void {
    if (!this.templateName) {
      this.errorMessage = 'Please enter a form name';
      this.showwarningname = true;
      setTimeout(() => this.showwarningname = false, 2000);
      return;
    }

    // Validate form has content
    if (!this.isFormValid()) {
      this.errorMessage = 'Cannot publish an empty form. Please add at least one field to a tab.';
      this.showwarningname = true;
      setTimeout(() => this.showwarningname = false, 2000);
      return;
    }

    // Show confirmation dialog
    this.showconfirmpublish = true;
  }

  onPublishConfirmed(): void {
    this.showconfirmpublish = false;
    // Save changes first, then publish
    if (this.templateId) {
      this.updateTemplate(() => this.publishTemplate());
    } else {
      this.createTemplate(() => this.publishTemplate());
    }
  }

  onPublishCancelled(): void {
    this.showconfirmpublish = false;
  }

  private createTemplate(callback?: () => void): void {
    const req: CreateFormRequest = {
      name: this.templateName,
      description: this.templateDescription,
      tabs: this.tabs
    };
    this.formService
      .createTemplate(this.organizationId, req)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (newId) => {
          this.showsuccesssaveFunc();
          this.templateId = newId;
          this.formSaved.emit();
          if (callback) callback();
        },
        error: (err) => {
          console.error('Error creating template:', err);
          this.errorMessage = 'Failed to create form: ' + (err.error?.message || err.message);
          this.showwarningname = true;
          setTimeout(() => this.showwarningname = false, 2000);
        }
      });
  }

  private updateTemplate(callback?: () => void): void {
    if (!this.templateId) return;
    const req: UpdateFormRequest = {
      name: this.templateName,
      description: this.templateDescription,
      tabs: this.tabs
    };
    this.formService
      .updateTemplate(this.organizationId, this.templateId, req)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.showsuccesssaveFunc();
          this.formSaved.emit();
          if (callback) callback();
        },
        error: (err) => {
          console.error('Error updating template:', err);
          this.errorMessage = 'Failed to update form: ' + (err.error?.message || err.message);
          this.showwarningname = true;
          setTimeout(() => this.showwarningname = false, 2000);
        }
      });
  }

  private publishTemplate(): void {
    if (!this.templateId) return;

    this.isPublishing = true;
    console.log('Publishing template:', this.templateId);

    this.formService
      .publishTemplate(this.organizationId, this.templateId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response) => {
          console.log('Template published successfully, response:', response);

          // Try to parse version from response (backend may return version number or message)
          let publishedVersion = this.templateVersion + 1;
          const versionMatch = response.match(/version\s+(\d+)/i);
          if (versionMatch) {
            publishedVersion = parseInt(versionMatch[1], 10);
          }

          alert(`Form published successfully as version ${publishedVersion}!\n\nReturning to form list.`);

          this.formSaved.emit();
          this.formPublished.emit();
          this.isPublishing = false;
        },
        error: (err) => {
          console.error('Error publishing template:', err);
          const errorMessage = err.error?.message || err.message || 'Unknown error occurred';
          alert(`Failed to publish form: ${errorMessage}\n\nPlease try again or contact support if the issue persists.`);
          this.isPublishing = false;
        }
      });
  }


  discardChanges(): void {
    if (confirm('Discard all changes?')) {
      this.initializeForm();
      this.templateName = '';
      this.templateDescription = '';
    }
  }

  archiveForm(): void {
    if (!this.templateId) {
      alert('Cannot archive a form that has not been saved yet.');
      return;
    }

    console.log('archiveForm() called, showing confirmation dialog');
    // Show confirmation dialog using alertbox
    this.showconfirmarchive = true;
  }

  onArchiveConfirmed(): void {
    console.log('onArchiveConfirmed() called');
    this.showconfirmarchive = false;
    this.archiveTemplate();
  }

  onArchiveCancelled(): void {
    console.log('onArchiveCancelled() called');
    this.showconfirmarchive = false;
  }

  private archiveTemplate(): void {
    if (!this.templateId) return;

    console.log('archiveTemplate() executing archive for template:', this.templateId);
    this.isArchiving = true;
    this.showerrorarchive = false;
    console.log('Archiving template:', this.templateId);

    this.formService
      .archiveTemplate(this.organizationId, this.templateId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response) => {
          console.log('Template archived successfully, response:', response);

          // Update local state directly instead of reloading
          this.templateStatus = 'archived';
          this.isReadOnly = true;

          // Show success message using app-alertbox pattern
          this.showsuccessarchiveFunc();

          this.formSaved.emit();
          this.isArchiving = false;
        },
        error: (err) => {
          console.error('Error archiving template:', err);
          this.errorMessage = err.error?.message || err.message || 'Failed to archive form template';
          this.showerrorarchiveFunc();
          this.isArchiving = false;
        }
      });
  }

  showsuccessarchiveFunc(): void {
    this.showsuccessarchive = true;
    setTimeout(() => {
      this.showsuccessarchive = false;
    }, 2000);
  }

  showerrorarchiveFunc(): void {
    this.showerrorarchive = true;
    setTimeout(() => {
      this.showerrorarchive = false;
    }, 2000);
  }

  showsuccessdraftFunc(): void {
    this.showsuccessdraft = true;
    setTimeout(() => {
      this.showsuccessdraft = false;
    }, 2000);
  }

  showerrordraftFunc(): void {
    this.showerrordraft = true;
    setTimeout(() => {
      this.showerrordraft = false;
    }, 2000);
  }

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  getFieldTypeLabel(type: FormFieldType): string {
    const labels: Record<FormFieldType, string> = {
      text: 'Text',
      'select-one': 'Single Select',
      'select-multiple': 'Multi Select',
      file: 'File Upload',
      image: 'Picture Upload',
      description: 'Description',
      'image-display': 'Image Display'
    };
    return labels[type];
  }

  getTextInputTypeLabel(type: TextInputType): string {
    const labels: Record<TextInputType, string> = {
      richtext: 'Rich Text (with formatting)',
      plaintext: 'Plain Text (multi-line)',
      singleline: 'Single Line (URLs, short text)'
    };
    return labels[type];
  }

  isFormValid(): boolean {
    // Check if form has at least one tab with at least one field
    return this.tabs.length > 0 && this.tabs.some(tab => tab.fields.length > 0);
  }

  canPublish(): boolean {
    return !this.isPublishing &&
      !this.isReadOnly &&
      this.templateStatus !== 'published' &&
      this.isFormValid();
  }

  canArchive(): boolean {
    return this.templateId !== undefined &&
      !this.isArchiving &&
      this.templateStatus !== 'archived';
  }

  createDraftFromPublished(): void {
    if (!this.templateId || !this.isReadOnly) return;

    // Show confirmation dialog using alertbox
    this.showconfirmdraft = true;
  }

  onDraftConfirmed(): void {
    this.showconfirmdraft = false;
    this.executeDraftCreation();
  }

  onDraftCancelled(): void {
    this.showconfirmdraft = false;
  }

  private executeDraftCreation(): void {
    if (!this.templateId) return;

    this.isCreatingDraft = true;

    this.formService
      .createDraftFromTemplate(this.organizationId, this.templateId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (newDraftId) => {
          console.log('Draft created successfully, new ID:', newDraftId);
          this.showsuccessdraftFunc();

          // Update to the new draft template ID and reload
          this.templateId = newDraftId.trim();
          this.loadTemplate();
          this.isCreatingDraft = false;
        },
        error: (err) => {
          console.error('Error creating draft:', err);
          this.errorMessage = err.error?.message || err.message || 'Failed to create draft';
          this.showerrordraftFunc();
          this.isCreatingDraft = false;
        }
      });
  }

  showsuccesssaveFunc(): void {
    this.showsuccesssave = true;
    setTimeout(() => {
      this.showsuccesssave = false;
    }, 2000);
  }
}
